import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CopilotPanel from '../copilot_native/components/copilot-panel';
import CopilotErrorBoundary from '../copilot_native/components/copilot-error-boundary';
import useCopilot, { stashHandoff } from '../copilot_native/hooks/use-copilot';
import { isCopilotConfigured } from '../copilot_native/api/copilot-api';
import { isRouteAction, isSafeInternalPath, scopeForPath } from '../copilot_native/model/route-action';

function PreviewConsole({ appId, sessionId }) {
    const navigate = useNavigate();
    const configured = isCopilotConfigured();
    const copilot = useCopilot(appId, configured && Boolean(sessionId), {
        previewSessionId: sessionId,
        onAction: action => {
            if (!isRouteAction(action) || !isSafeInternalPath(action.path)) return;
            const target = scopeForPath(action.path);
            if (target && action.handoffMessage) stashHandoff(target, action.handoffMessage);
            navigate(action.path);
        },
    });
    if (!configured) return <div className="flex h-full items-center justify-center rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500">Console is not configured for this environment.</div>;
    return <CopilotPanel embedded copilot={copilot} ready={Boolean(sessionId)} previewSessionId={sessionId} />;
}

const clamp = value => Math.min(55, Math.max(28, value));

export default function PreviewWorkspace({ appId, sessionId, children }) {
    const container = useRef(null);
    const [width, setWidth] = useState(() => {
        try { const saved = Number(localStorage.getItem('bc:preview-console-width')); return saved ? clamp(saved) : 32; } catch (_) { return 32; }
    });
    const [dragging, setDragging] = useState(false);
    const resize = value => {
        const next = clamp(value);
        setWidth(next);
        try { localStorage.setItem('bc:preview-console-width', String(next)); } catch (_) {}
    };
    return (
        <div className="h-full w-full overflow-auto bg-gray-50 p-4">
            <div ref={container} className="relative flex h-[calc(100vh-106px)] min-h-[400px] min-w-[1100px]">
                <div id="preview-console" style={{ width: `${width}%` }} className="min-w-0 shrink-0 rounded-2xl ring-1 ring-brand-lime/40 shadow-[0_0_24px_-6px_rgba(186,230,26,0.28)] transition-shadow duration-300 focus-within:ring-brand-lime/60 focus-within:shadow-[0_0_30px_-4px_rgba(186,230,26,0.38)] dark:ring-brand-lime/30 dark:shadow-[0_0_28px_-6px_rgba(186,230,26,0.18)] dark:focus-within:shadow-[0_0_32px_-4px_rgba(186,230,26,0.28)] motion-reduce:transition-none">
                    <CopilotErrorBoundary fallback={({ reset }) => <div role="alert" className="p-5 text-sm text-gray-500">Console could not load. <button type="button" onClick={reset} className="underline">Try again</button></div>}><PreviewConsole key={appId} appId={appId} sessionId={sessionId} /></CopilotErrorBoundary>
                </div>
                <div role="separator" tabIndex={0} aria-label="Resize Console and Preview" aria-orientation="vertical" aria-controls="preview-console preview-debugger" aria-valuemin={28} aria-valuemax={55} aria-valuenow={Math.round(width)}
                    onDoubleClick={() => resize(32)}
                    onKeyDown={event => {
                        const values = { ArrowLeft: width - 2, ArrowRight: width + 2, Home: 28, End: 55 };
                        if (event.key in values) { event.preventDefault(); resize(values[event.key]); }
                    }}
                    onPointerDown={event => { event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); setDragging(true); }}
                    onPointerMove={event => { if (dragging) { const rect = container.current.getBoundingClientRect(); resize((event.clientX - rect.left) / rect.width * 100); } }}
                    onPointerUp={() => setDragging(false)} onPointerCancel={() => setDragging(false)} onLostPointerCapture={() => setDragging(false)}
                    className="group z-20 flex w-4 shrink-0 touch-none cursor-col-resize items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                    <span className={`h-12 w-1 rounded-full transition-colors ${dragging ? 'bg-blue-500' : 'bg-gray-300 group-hover:bg-blue-500'}`} />
                </div>
                <div id="preview-debugger" className="min-w-0 flex-1 overflow-x-auto rounded-2xl">{children}</div>
                {dragging && <div className="absolute inset-0 z-10 cursor-col-resize" />}
            </div>
        </div>
    );
}
